import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Printer, Mail, Phone, MapPin, Building2, FileText, Globe } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { branchService } from '@/services/branchService'
import { BranchEntity } from '@/api/types'

/**
 * Branch View Page
 * Detailed view of branch information
 */
export const BranchesView: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  
  const [branch, setBranch] = useState<BranchEntity | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!id) return

    const fetchBranch = async () => {
      try {
        setLoading(true)
        const data = await branchService.getBranchById(Number(id))
        setBranch(data)
      } catch (error: any) {
        console.error('Error fetching branch:', error)
        toast.error(error.response?.data?.message || 'Failed to fetch branch details')
        navigate('/branches')
      } finally {
        setLoading(false)
      }
    }

    fetchBranch()
  }, [id, navigate])

  const handlePrint = () => {
    window.print()
  }

  const handleBack = () => {
    navigate('/branches')
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    } catch {
      return dateString
    }
  }

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        <Card>
          <CardContent className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="mt-4 text-muted-foreground">Loading branch details...</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!branch) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">Branch not found</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header with Back and Print buttons */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button onClick={handlePrint} className="print:hidden">
          <Printer className="w-4 h-4 mr-2" />
          Print
        </Button>
      </div>

      {/* Branch Header */}
      <Card>
        <CardContent className="p-8">
          <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
            {branch.logo || branch.logoUrl ? (
              <div className="h-32 w-32 border-2 border-muted rounded-lg overflow-hidden bg-muted/10 flex-shrink-0">
                <img 
                  src={branch.logo || branch.logoUrl} 
                  alt={branch.nameEn || 'Branch Logo'} 
                  className="h-full w-full object-cover"
                />
              </div>
            ) : (
              <div className="h-32 w-32 border-2 border-muted rounded-lg flex items-center justify-center bg-muted/10 flex-shrink-0">
                <Building2 className="h-16 w-16 text-muted-foreground" />
              </div>
            )}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-3xl font-bold mb-2">
                {branch.nameEn || branch.nameAr || 'N/A'}
              </h1>
              {branch.nameAr && branch.nameEn && (
                <p className="text-lg text-muted-foreground mb-4">{branch.nameAr}</p>
              )}
              {branch.shortName && (
                <p className="text-sm text-muted-foreground mb-2">
                  Short Name: {branch.shortName}
                </p>
              )}
              {branch.currency && (
                <p className="text-sm text-muted-foreground mb-2">
                  Currency: {branch.currency}
                </p>
              )}
              <Badge variant={branch.isEnable ? 'default' : 'secondary'} className="mt-2">
                {branch.isEnable ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Contact Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {branch.email && (
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{branch.email}</p>
                </div>
              </div>
            )}
            {branch.phone && (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{branch.phone}</p>
                </div>
              </div>
            )}
            {branch.website && (
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Website</p>
                  <a href={branch.website} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                    {branch.website}
                  </a>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Address Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Address
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {branch.addressEn && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Address (English)</p>
                <p className="font-medium">{branch.addressEn}</p>
              </div>
            )}
            {branch.addressAr && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Address (Arabic)</p>
                <p className="font-medium">{branch.addressAr}</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {branch.street && (
                <div>
                  <p className="text-sm text-muted-foreground">Street</p>
                  <p className="font-medium">{branch.street}</p>
                </div>
              )}
              {branch.buildingNumber && (
                <div>
                  <p className="text-sm text-muted-foreground">Building Number</p>
                  <p className="font-medium">{branch.buildingNumber}</p>
                </div>
              )}
              {branch.plot && (
                <div>
                  <p className="text-sm text-muted-foreground">Additional Number</p>
                  <p className="font-medium">{branch.plot}</p>
                </div>
              )}
              {branch.citySubdivision && (
                <div>
                  <p className="text-sm text-muted-foreground">City Subdivision</p>
                  <p className="font-medium">{branch.citySubdivision}</p>
                </div>
              )}
              {branch.countryName && (
                <div>
                  <p className="text-sm text-muted-foreground">Country</p>
                  <p className="font-medium">{branch.countryName}</p>
                </div>
              )}
              {branch.cityName && (
                <div>
                  <p className="text-sm text-muted-foreground">City</p>
                  <p className="font-medium">{branch.cityName}</p>
                </div>
              )}
              {branch.zipCode && (
                <div>
                  <p className="text-sm text-muted-foreground">Zip Code</p>
                  <p className="font-medium">{branch.zipCode}</p>
                </div>
              )}
              {branch.countrySubentity && (
                <div>
                  <p className="text-sm text-muted-foreground">Country Subentity</p>
                  <p className="font-medium">{branch.countrySubentity}</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Business Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {branch.crNo && (
              <div>
                <p className="text-sm text-muted-foreground">CR Number</p>
                <p className="font-medium">{branch.crNo}</p>
              </div>
            )}
            {branch.vatNo && (
              <div>
                <p className="text-sm text-muted-foreground">VAT Number</p>
                <p className="font-medium">{branch.vatNo}</p>
              </div>
            )}
            {branch.language && (
              <div>
                <p className="text-sm text-muted-foreground">Language</p>
                <p className="font-medium">{branch.language}</p>
              </div>
            )}
            {branch.currency && (
              <div>
                <p className="text-sm text-muted-foreground">Currency</p>
                <p className="font-medium">{branch.currency}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bank Details */}
      {(branch.bankDetailEn || branch.bankDetailAr) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Bank Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {branch.bankDetailEn && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Bank Detail (English)</p>
                  <p className="whitespace-pre-wrap">{branch.bankDetailEn}</p>
                </div>
              )}
              {branch.bankDetailAr && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Bank Detail (Arabic)</p>
                  <p className="whitespace-pre-wrap">{branch.bankDetailAr}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Additional Information */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {branch.createdDate && (
              <div>
                <p className="text-sm text-muted-foreground">Created Date</p>
                <p className="font-medium">{formatDate(branch.createdDate)}</p>
              </div>
            )}
            {branch.modifiedDate && (
              <div>
                <p className="text-sm text-muted-foreground">Modified Date</p>
                <p className="font-medium">{formatDate(branch.modifiedDate)}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

